import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "@local-logic/design-system";

import type { TravelMode } from "~/sdks/LocalContent/hooks/useCommute/types";

import * as globalStyles from "../styles";
import * as styles from "./styles";

export function Commute({
  isLoading = false,
  commuteType,
  durationInSeconds,
  onCalculateCommute,
}: {
  isLoading?: boolean;
  commuteType: TravelMode;
  durationInSeconds?: number;
  onCalculateCommute?: () => void;
}) {
  const { t } = useTranslation(["localContent", "common"]);

  const durationString = useMemo(() => {
    if (!durationInSeconds) return null;

    const hours = Math.floor(durationInSeconds / 60 / 60);
    const minutes = Math.floor((durationInSeconds / 60) % 60);

    const minutesString = `${minutes} ${t("common:time.minute", { count: minutes })}`;

    if (hours === 0) {
      return minutesString;
    }

    return `${hours} ${t("common:time.hour", { count: hours })} ${minutesString}`;
  }, [durationInSeconds, t]);

  const travelTimeString = useMemo(() => {
    if (!durationString) return "";

    return `${durationString} ${t(`commute.travelTypes.${commuteType}`)}`;
  }, [durationString, commuteType, t]);

  if (!isLoading && typeof durationInSeconds === "undefined") {
    return null;
  }

  return (
    <div className={globalStyles.content}>
      <div className={styles.container}>
        {isLoading && (
          <div
            data-testid="poi-popup-commute-loading"
            className={styles.loading}
          ></div>
        )}
        {!isLoading && durationString && (
          <p
            data-testid="poi-popup-commute-duration"
            aria-label={travelTimeString}
          >
            {travelTimeString.toLowerCase()}
          </p>
        )}
        {onCalculateCommute && (
          <Button
            className={styles.button}
            onClick={onCalculateCommute}
            variant="ghost"
            isDisabled={isLoading}
          >
            {t("commute.viewRoute")}
          </Button>
        )}
      </div>
    </div>
  );
}
